import type { KeyboardEvent } from 'react';
import { ClientSignDataType } from './mockDataTypes';

const PHONE_MIN_LENGTH = 10;
const PHONE_MAX_LENGTH = 11;
const EMAIL_LIMIT = 40;
const EMAIL_PATTERN =
	/^[\w+\-.]+(\.[\w+\-.]+)*@[\w-]+(\.[\w-]+)*(\.[a-z]{2,})$/;

const PHONE_ERROR = 'The number must have 10 or 11 digits.';
const EMAIL_ERROR = 'Please enter a valid email address.';
const EMAIL_LOWERCASE_ERROR = 'Please enter your email address in lowercase.';
const EMAIL_LENGTH_ERROR =
	'Your email address has to have less than 40 characters';

const emailRequiresLowercase = (value: string): boolean =>
	value !== value.toLowerCase();
const DISALLOWED_DOCUMENT_CHARS_ERROR =
	'These characters cannot be written in the document.';

/** Emoji / pictographic symbols rejected by the sign-document API. */
const DISALLOWED_DOCUMENT_CHAR_SOURCE = '\\p{Extended_Pictographic}';
const DISALLOWED_DOCUMENT_CHAR_PATTERN = new RegExp(
	DISALLOWED_DOCUMENT_CHAR_SOURCE,
	'u'
);
const DISALLOWED_DOCUMENT_CHAR_PATTERN_GLOBAL = new RegExp(
	DISALLOWED_DOCUMENT_CHAR_SOURCE,
	'gu'
);

export const stripDisallowedDocumentCharacters = (raw: string): string =>
	raw.replace(DISALLOWED_DOCUMENT_CHAR_PATTERN_GLOBAL, '');

export const hasDisallowedDocumentCharacters = (value: string): boolean =>
	DISALLOWED_DOCUMENT_CHAR_PATTERN.test(value);

export const isDocumentCharacterRejectionError = (message: string): boolean =>
	/cannot be written in the document/i.test(message);

export const sanitizeContactInput = (
	dataType: ClientSignDataType | undefined,
	raw: string
): string => {
	if (dataType === 'PHONE') {
		return raw.replace(/\D/g, '').slice(0, PHONE_MAX_LENGTH);
	}
	if (dataType === 'EMAIL') {
		return stripDisallowedDocumentCharacters(raw.replace(/\s/g, '')).slice(
			0,
			EMAIL_LIMIT
		);
	}
	return stripDisallowedDocumentCharacters(raw);
};

export const contactFormatError = (
	dataType: ClientSignDataType | undefined,
	value: string | boolean | undefined
): string | null => {
	if (typeof value !== 'string' || value.length === 0) return null;

	if (dataType === 'PHONE') {
		const digits = value.replace(/\D/g, '');
		if (
			digits.length >= PHONE_MIN_LENGTH &&
			digits.length <= PHONE_MAX_LENGTH
		) {
			return null;
		}
		return PHONE_ERROR;
	}

	if (dataType === 'EMAIL') {
		if (value.length > EMAIL_LIMIT) return EMAIL_LENGTH_ERROR;
		if (emailRequiresLowercase(value)) return EMAIL_LOWERCASE_ERROR;
		if (!EMAIL_PATTERN.test(value)) return EMAIL_ERROR;
	}

	if (hasDisallowedDocumentCharacters(value)) {
		return DISALLOWED_DOCUMENT_CHARS_ERROR;
	}

	return null;
};

export const blockInvalidContactKey = (
	dataType: ClientSignDataType | undefined,
	event: KeyboardEvent<HTMLInputElement>
): void => {
	if (event.ctrlKey || event.metaKey) return;

	if (dataType === 'PHONE') {
		if (event.key.length === 1 && !/\d/.test(event.key)) {
			event.preventDefault();
		}
		return;
	}

	if (dataType === 'EMAIL' && event.key === ' ') {
		event.preventDefault();
		return;
	}

	if (
		event.key.length === 1 &&
		DISALLOWED_DOCUMENT_CHAR_PATTERN.test(event.key)
	) {
		event.preventDefault();
	}
};
